// src/components/layout/app-shell.tsx
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { SessionExpiryDialog } from './session-expiry-dialog';
import { StepUpDialog } from './step-up-dialog';
import { CapabilitiesErrorBanner } from './capabilities-error-banner';
import { useIdleLogout } from '@/auth/idle-timer';
import { useLogout } from '@/auth/hooks';
import { useAuthStore } from '@/auth/auth-store';

// #7 — 28 min de inactividad abren el aviso; su cuenta atrás de 2:00 completa
// los 30 min totales antes del auto-logout.
const WARN_AFTER_MS = 28 * 60 * 1000;

export function AppShell() {
  const logout = useLogout();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const [warnOpen, setWarnOpen] = useState(false);

  // Redirect reactivo: cualquier transición a 'unauthenticated' manda a /login de
  // inmediato, sin esperar a la siguiente navegación. Cubre los tres caminos que
  // limpian la sesión — botón "Cerrar sesión", auto-logout por inactividad y
  // fallo de refresh (401 → onAuthFailure). El guard beforeLoad del router solo
  // corre al navegar, así que sin esto el usuario se quedaba varado en la página.
  useEffect(() => {
    if (status === 'unauthenticated') {
      navigate({ to: '/login', search: { redirect: undefined } });
    }
  }, [status, navigate]);

  const doLogout = useCallback(() => {
    setWarnOpen(false);
    void logout(); // el efecto de arriba redirige cuando el store queda limpio
  }, [logout]);

  useIdleLogout(() => setWarnOpen(true), WARN_AFTER_MS);

  return (
    <div data-testid="app-shell" className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar />
        <CapabilitiesErrorBanner />
        <main className="flex-1 overflow-auto bg-bg p-6">
          <Outlet />
        </main>
      </div>
      <SessionExpiryDialog open={warnOpen} onStay={() => setWarnOpen(false)} onLogout={doLogout} />
      <StepUpDialog />
    </div>
  );
}
